<template>
  <app-layout>
    <template #header>
      <inertia-link
        :href="route('dashboard')"
        class="lg:absolute relative left-7 flex items-center text-vice-teal-500 text-xs"
      >
        <ChevronLeftIcon class="w-4 h-4 mr-1"></ChevronLeftIcon>
        <span class="flex-none">All Sessions</span>
      </inertia-link>
      <div
        class="
          pb-5
          pt-4
          2xl:pt-0
          border-b border-gray-200
          sm:flex
          sm:items-center
          sm:justify-between
        "
      >
        <div class="flex gap-x-5">
          <img
            :src="$page.props.session.details.image_url"
            class="w-auto h-32 img-fluid object-contain object-left"
            alt=""
          />
          <div>
            <div class="flex">
              <h2 class="text-3xl max-w-3xl text-vice-teal-900 font-semibold leading-tight">
                {{ $page.props.session.title }}
              </h2>
              <p class="pl-2 pt-3.5 truncate leading-9 text-xs text-dark-grey">
                with {{ $page.props.session.details.clinician?.name }}

                <span
                  v-show="$page.props.session.details.clinician?.deleted_at"
                  class="text-gray-900 font-medium"
                >
                  (archived)
                </span>

                <span
                  v-show="$page.props.session.details.university?.name"
                  class="text-gray-900 font-medium"
                >
                  ({{ $page.props.session.details.university?.name }})
                </span>
              </p>
            </div>
            <div class="flex gap-5 pt-3">
              <p class="flex gap-1">
                <CalendarIcon class="h-5 w-5 text-gray-300"></CalendarIcon>
                <span class="text-gray-900 text-xs">
                  {{ yearFormatter($page.props.session.details.session_start_by_timezone) }}
                </span>
              </p>
              <p class="flex gap-1">
                <ClockIcon class="h-5 w-5 text-gray-300"></ClockIcon>
                <span class="text-gray-900 text-xs">
                  {{ hoursFormatter($page.props.session.details.session_start_by_timezone) }}
                </span>
              </p>
              <p class="flex gap-1" v-if="$page.props.session.details.year_of_study">
                <AcademicCapIcon class="h-5 w-5 text-gray-300"></AcademicCapIcon>
                <span class="text-gray-900 text-xs">
                  {{ $page.props.session.details.year_of_study }} year of study
                </span>
              </p>
            </div>
            <div class="pt-6">
              <p class="text-xs">{{ $page.props.session.summary }}</p>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="sm:hidden">
          <label for="tabs" class="sr-only">Select a tab</label>
          <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
          <select
            id="tabs"
            name="tabs"
            class="
              block
              w-full
              focus:ring-indigo-500
              focus:border-indigo-500
              border-gray-300
              rounded-md
            "
          >
            <option
              v-for="tab in tabs"
              :key="tab.name"
              :selected="tab.current"
              @click="setTabState(tab.id)"
              :class="tab.visible ? 'block' : 'hidden'"
            >
              {{ tab.name }}
            </option>
          </select>
        </div>
        <div class="hidden sm:block">
          <div>
            <nav class="-mb-px flex" aria-label="Tabs">
              <button
                v-for="tab in tabs"
                :key="tab.name"
                @click="setTabState(tab.id)"
                :class="[
                  tab.visible ? 'block' : 'hidden',
                  tab.current
                    ? 'border-vice-teal-400 text-grey-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                  'w-1/5 py-4 px-1 text-center border-b-2 font-medium text-xs',
                ]"
                :aria-current="tab.current ? 'page' : undefined"
              >
                {{ tab.name }}
              </button>
            </nav>
          </div>
        </div>
      </div>
    </template>
    <div class="max-w-5xl mx-auto pt-6 pb-10 px-4 sm:px-6">
      <!--  Overview tab -->

      <div class="grid grid-cols-10 gap-8 pt-11" :class="tabs[0].current ? 'block' : 'hidden'">
        <div class="col-span-10 sm:col-span-6">
          <WhatYouLearn />
          <AboutSession />
          <AboutClinic />
          <SessionFiles v-if="$page.props.session.files.length" />
        </div>
        <div class="col-span-10 sm:col-span-4 flex flex-col gap-10">
          <ApplicationsList v-if="!$page.props.session.is_private"></ApplicationsList>
          <AttendanceStats></AttendanceStats>

          <AboutClinician v-if="$page.props.session.details.clinician" />
        </div>
      </div>

      <!-- Session statistic tab -->
      <div
        class=""
        :class="tabs[1].current ? 'block' : 'hidden'"
        v-if="$page.props.sessionAnalyticsData"
      >
        <session-duration-and-totals
          :durationData="$page.props.sessionAnalyticsData.sessionDurationAndTotals"
        ></session-duration-and-totals>
        <attendance-per-participants
          :attendancePerParticipantsData="
            $page.props.sessionAnalyticsData.attendancePerParticipants
          "
        ></attendance-per-participants>
        <other-statistics
          :otherStatisticsData="$page.props.sessionAnalyticsData.otherStatistics"
        ></other-statistics>
      </div>
    </div>
  </app-layout>
</template>

<script>
import { ClockIcon, ChevronLeftIcon, AcademicCapIcon } from '@heroicons/vue/solid';
import { CalendarIcon } from '@heroicons/vue/outline';
import { ref } from 'vue';
import DateFormatMixin from '@/Mixins/DateFormatMixin.vue';

import AppLayout from '@/Layouts/AppLayout.vue';
import AboutSession from './Details/AboutSession.vue';
import SessionFiles from './Details/SessionFiles.vue';
import AboutClinic from './Details/AboutClinic.vue';
import ApplicationsList from './Details/ApplicationsList.vue';
import AttendanceStats from './Details/AttendanceStats.vue';
import AboutClinician from './Details/AboutClinician.vue';
import WhatYouLearn from './Details/WhatYouLearn.vue';
import SessionDurationAndTotals from './Details/SessionAnalytics/SessionDurationAndTotals.vue';
import AttendancePerParticipants from './Details/SessionAnalytics/AttendancePerParticipants.vue';
import OtherStatistics from './Details/SessionAnalytics/OtherStatistics.vue';

export default {
  name: 'ShowDetail',
  mounted() {
    this.setSessionUserState();
  },
  setup() {
    const hasSession = ref(false);
    const hasApplied = ref(false);
    const hasInvite = ref(false);
    const hasPendingInvite = ref(false);
    const userSession = ref(null);
    const userSessionType = ref('');

    return {
      hasSession,
      hasApplied,
      hasInvite,
      hasPendingInvite,
      userSession,
      userSessionType,
    };
  },
  mixins: [DateFormatMixin],
  components: {
    AboutClinician,
    ApplicationsList,
    AttendanceStats,
    // Invitation,
    AboutClinic,
    AboutSession,
    // SessionEntryCard,
    // SessionEntryWizard,
    AppLayout,
    ChevronLeftIcon,
    CalendarIcon,
    ClockIcon,
    AcademicCapIcon,
    SessionFiles,
    WhatYouLearn,
    SessionDurationAndTotals,
    AttendancePerParticipants,
    OtherStatistics,
  },
  data() {
    return {
      user_invites: this.$page.props.session.users,
      tabs: [
        { id: 'overview', name: 'Overview', current: true, visible: true },
        {
          id: 'session_data',
          name: 'Session data',
          current: false,
          visible: !!this.$page.props.sessionAnalyticsData, // show only if the session has tracking (analitics) data
        },
        { id: 'more_information', name: 'More Information', current: false, visible: false },
      ],
    };
  },
  methods: {
    setSessionUserState() {
      this.hasSession = this.$page.props.hasSession;
      this.hasApplied = this.$page.props.hasApplied;
      this.hasInvite = this.$page.props.hasInvite;
      this.hasPendingInvite = this.$page.props.hasPendingInvite;
      this.userSessionType = this.$page.props.userType;
      if (this.$page.props.reflection) {
        this.setTabState(this.tabs[2].id);
      }
    },

    appliedUser(userType) {
      this.hasApplied = true;
      this.userSessionType = userType;
    },
    checkIfHasContent(content) {
      if (content) {
        return Boolean(content.replace(/<\/?[^>]+(>|$)/g, ''));
      }
      return false;
    },
    setTabState(id) {
      this.tabs.forEach((tab) => {
        tab.current = tab.id === id;
      });
    },
  },
};
</script>

<style scoped></style>
